import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Science,
  Speed,
  Psychology,
  TrendingUp,
} from '@mui/icons-material';
import MainLayout from '../components/Layout/MainLayout';

const About = () => {
  const theme = useTheme();

  const capabilities = [
    {
      icon: <Science sx={{ fontSize: 48 }} />,
      title: 'Spectroscopy Analysis',
      description:
        'Advanced tools for managing and analyzing spectroscopic data from various sensors and instruments.',
    },
    {
      icon: <Psychology sx={{ fontSize: 48 }} />,
      title: 'Machine Learning',
      description:
        'State-of-the-art AI models including linear, non-linear, and deep learning approaches for predictive analytics.',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 48 }} />,
      title: 'Data Visualization',
      description:
        'Comprehensive visualization tools including PCA, UMAP, Q-T plots, and Mahalanobis distance analysis.',
    },
    {
      icon: <Speed sx={{ fontSize: 48 }} />,
      title: 'Workflow Optimization',
      description:
        'Streamlined workflows from data import to model deployment with intuitive project management.',
    },
  ];

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            About Digital Spectra
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            A comprehensive platform for spectroscopy data management and AI-powered analysis
          </Typography>
        </Box>

        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            mt: 6,
            borderRadius: 4,
            textAlign: 'center',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: 'white',
          }}
        >
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Workflow Overview
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 700, mx: 'auto', mb: 3 }}>
            Digital Spectra follows a structured workflow to guide you from raw data to actionable
            insights:
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {['Data Import', 'Process & Visualize', 'Label & Curate', 'Train Models', 'Analyze Results'].map(
              (step, index) => (
                <Grid item key={index}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 120,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      background: alpha('#ffffff', 0.2),
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <Typography fontWeight={600}>
                      {index + 1}. {step}
                    </Typography>
                  </Box>
                </Grid>
              )
            )}
          </Grid>
        </Paper>

        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            mb: 6,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          }}
        >
          <Typography variant="h5" fontWeight={600} gutterBottom>
            What is Digital Spectra?
          </Typography>
          <Typography variant="body1" paragraph>
            Digital Spectra is an advanced platform designed for researchers and scientists working
            with spectroscopic data. It provides a complete end-to-end solution for data management,
            exploratory analysis, and machine learning model development.
          </Typography>
          <Typography variant="body1" paragraph>
            The platform enables teams to collaborate on projects, manage data from multiple sensors,
            label and curate samples, train sophisticated AI models, and visualize results through
            interactive plots and reports.
          </Typography>
          <Typography variant="body1">
            Whether you're working on classification tasks, predictive modeling, or exploratory data
            analysis, Digital Spectra provides the tools you need to accelerate your research and
            deliver meaningful insights.
          </Typography>
        </Paper>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom textAlign="center">
            Key Capabilities
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {capabilities.map((capability, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Paper
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 80,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                    color: theme.palette.primary.main,
                    mb: 2,
                  }}
                >
                  {capability.icon}
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {capability.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        
      </Container>
    </MainLayout>
  );
};

export default About;
